import { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Container,
  Slide,
  Divider,
} from "@mui/material";
import {
  Home,
  MessageSquare,
  Briefcase,
  Monitor,
  Mail,
  UsersRound,
  Menu,
  Clover as Close,
  ArrowDownUp as ArrowForward,
} from "lucide-react";
import AnimatedLogoLoop from "components/AnimatedLogoLoop";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

const theme = createTheme({
  palette: {
    primary: {
      main: "#394fe8",
      dark: "#101a5e",
    },
    background: {
      default: "#0a0a0a",
      paper: "#1a1a1a",
    },
    text: {
      primary: "#ffffff",
      secondary: "#b0b0b0",
    },
  },
});

function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  const routes = [
    { name: "Home", route: "/", icon: Home },
    {
      name: "Consultation",
      route: "/ConsultationProgram",
      icon: MessageSquare,
    },
    { name: "Careers", route: "/Careers", icon: Briefcase },
    { name: "Portfolio", route: "/PastProjects", icon: Monitor },
    { name: "Contact Us", route: "/ContactUs", icon: Mail },
    { name: "About Us", route: "/AboutUs", icon: UsersRound },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          width: "90%",
          maxWidth: "1500px",
          borderRadius: "20px",
          background: scrolled ? "rgba(0,0,0,0.22)" : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          transition: "0.4s ease-in-out",
          boxShadow: scrolled ? "0 8px 32px rgba(0,0,0,0.3)" : "none",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            width: "100%",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
            {/* LOGO */}
            <Box
              component="a"
              href="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                color: "inherit",
                gap: "5px",
                transition: "transform 0.2s",
                "&:hover": { transform: "scale(1.05)" },
              }}
            >
              <AnimatedLogoLoop
                color="#ffffff"
                style={{ width: 55, height: 55 }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  background:
                    "linear-gradient(45deg, #ffffff 30%, #e0e7ff 90%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.02em",
                  pt: "7px",
                }}
              >
                The DevHouse
              </Typography>
            </Box>

            {/* DESKTOP NAVIGATION */}
            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "none",
                  lg: "flex",
                },
                "@media (max-width: 1399px)": {
                  display: "none",
                },
                alignItems: "center",
                gap: 1,
              }}
            >
              {routes.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Button
                    key={item.route}
                    component="a"
                    href={item.route}
                    startIcon={<IconComponent size={18} />}
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      textTransform: "none",
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      position: "relative",
                      overflow: "hidden",
                      fontSize: "16px",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 24px rgba(255,255,255,0.1)",
                      },
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: "50%",
                        width: 0,
                        height: 2,
                        background: "linear-gradient(90deg, #fff, #e0e7ff)",
                        transform: "translateX(-50%)",
                        transition: "0.3s",
                      },
                      "&:hover:before": { width: "80%" },
                    }}
                  >
                    {item.name}
                  </Button>
                );
              })}
              {/* CTA BUTTON (only >= 1434px) */}
              <Box
                sx={{
                  ml: 2,
                  pl: 2,
                  borderLeft: "1px solid #0A8EF2",
                }}
              >
                <Button
                  component="button"
                  type="button"
                  variant="contained"
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.location.pathname === "/") {
                      window.dispatchEvent(new Event("openConsultation"));
                    } else {
                      navigate("/");
                      setTimeout(
                        () =>
                          window.dispatchEvent(new Event("openConsultation")),
                        140,
                      );
                    }
                  }}
                  sx={{
                    background: "#0A8EF2",
                    color: "#fff",
                    px: 3,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: "none",
                    display: "none",
                    "@media (min-width: 1434px)": { display: "block" },
                    "&:hover": {
                      background: "rgba(4,104,191,0.8)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Box>

            {/* MOBILE MENU BUTTON */}
            <IconButton
              onClick={toggleMobileMenu}
              sx={{
                display: "none",
                "@media (max-width: 1399px)": {
                  display: "block",
                },
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              {isMobileMenuOpen ? <Close /> : <Menu />}
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="top"
        open={isMobileMenuOpen}
        onClose={toggleMobileMenu}
        TransitionComponent={Slide}
        transitionDuration={400}
        sx={{
          display: "none",
          "@media (max-width: 1399px)": {
            display: "block",
          },
          "& .MuiDrawer-paper": {
            background: "rgba(20, 20, 40, 0.3)",
            backdropFilter: "blur(25px)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "white",
            mt: 10,
            p: 2,
            width: "90%",
            maxWidth: "600px",
            margin: "0 auto",
          },
        }}
      >
        <Box>
          <List>
            {routes.map((item) => {
              const IconComponent = item.icon;
              return (
                <ListItem
                  key={item.route}
                  component="a"
                  href={item.route}
                  onClick={toggleMobileMenu}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
                    <IconComponent size={20} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontWeight: 500,
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
          <Divider sx={{ my: 2, borderColor: "rgba(255, 255, 255, 0.2)" }} />
          <Button
            component="button"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              toggleMobileMenu();
              if (window.location.pathname === "/") {
                window.dispatchEvent(new Event("openConsultation"));
              } else {
                navigate("/");
                setTimeout(
                  () => window.dispatchEvent(new Event("openConsultation")),
                  140,
                );
              }
            }}
            fullWidth
            variant="contained"
            endIcon={<ArrowForward size={16} />}
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              color: "#000",
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
              },
            }}
          >
            Get Started
          </Button>
        </Box>
      </Drawer>

      {/* Spacer */}
      <Toolbar />
    </ThemeProvider>
  );
}

export default Navbar;
